import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import moment from "moment";
import { each, isEmpty, map, orderBy } from "lodash";
import TermsModal from "pages/signUp/TermsModal";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { Card, Col, Row, Tab, Tabs } from "react-bootstrap";
import { FiDownload, FiEye, FiList } from "react-icons/fi";
import {
  getIraAccountIdSelector,
  getUserCRBAgreementDate,
  getUserVirtualAccountNumberDisclosureDate,
  userIs401kRollIn,
} from "store/selectors/user";
import { getDownloadUrl } from "services/documentService";
import {
  getApexDocuments,
  getDocumentsForBucket,
} from "actions/documentActions";
import { getAllAccounts } from "actions/accountActions";
import { flushErrors } from "actions/errorActions";
import { simulateLinkClickNewTab } from "utils/dom";
import {
  accountConstants,
  documentConstants,
  userConstants,
} from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";

import IconMessageCard from "components/IconMessageCard";
import IconBanner from "components/IconBanner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import "./IndividualStatements.scss";
import { isProduction } from "utils/determineSite";
import { getUserAgreements } from "actions/userActions";
import type { Document as UserDocument } from "types/models";
import type { RootState } from "types/state";

const tabs = [
  {
    label: "IRA Statements",
    id: "iraStatements",
  },
  {
    label: "PRP Statements",
    id: "prpStatements",
  },
  {
    label: "Trade Confirmations",
    id: "confirmations",
  },
  {
    label: "Account Agreements",
    id: "agreements",
  },
];

// Which fetch backs each tab. IRA statements and confirmations are both served
// by a single APEX request (split client-side), so they share one source and
// selecting either one never triggers a second fetch. PRP statements come from
// CRB; agreements pull the user's agreement history.
type TabSource = "apex" | "crb" | "agreements";

const TAB_SOURCE: Record<string, TabSource> = {
  iraStatements: "apex",
  confirmations: "apex",
  prpStatements: "crb",
  agreements: "agreements",
};

const TAB_BANNERS: Partial<Record<string, React.ReactNode>> = {
  iraStatements:
    "All IRA account statements and tax documents as issued by our broker dealer Apex Clearing Corporation. These documents summarize the activities on your retirement account and represent its periodic balance.",
  prpStatements: (
    <>
      All contributions to your IRAs via Icon PRP payroll deduction as issued by
      our banking partner Cross River Bank.
      <br />
      <br />
      <strong>IMPORTANT</strong>: These statements only reflect PRP
      contributions and will always balance to zero dollars as contributions
      pass through this account and are sent to your IRAs. For your IRA account
      retirement balances, always refer to your IRA statements.
    </>
  ),
  confirmations:
    "All IRA trading activity. Each confirmation reflects trades that were made on your fully managed account. Trades can be made for a variety of reasons.",
  agreements:
    "All legal agreements and disclosures related to your Icon accounts.",
};

interface StaticStatement {
  createdAt: string;
  name: string;
  id: string;
  url?: string;
  onClick?: () => void;
}

type StatementLike = UserDocument | StaticStatement;

interface StatementRow {
  statement: StatementLike;
  id: string;
  createdAt: string;
  name: string;
  statementsToUse: StatementLike[] | undefined;
  statementType: "dynamic" | "static" | undefined;
}

const actions = [
  accountConstants.GET_ACCOUNTS,
  documentConstants.GET_DOCUMENTS,
  userConstants.GET_USER_AGREEMENTS,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    prpStatements: orderBy(
      state.userDocuments.prpStatements,
      "createdAt",
      "desc"
    ),
    iraStatements: orderBy(
      state.userDocuments.iraStatements,
      "createdAt",
      "desc"
    ),
    confirmations: orderBy(
      state.userDocuments.confirmations,
      "createdAt",
      "desc"
    ),
    isFetching: loadingSelector(state),
    error: errorSelector(state),
    iraAccountId: getIraAccountIdSelector(state),
    is401kRollin: userIs401kRollIn(state),
    crbAgreementDate: getUserCRBAgreementDate(state),
    virtualAccountNumberDisclosureDate:
      getUserVirtualAccountNumberDisclosureDate(state),
    userId: state.user.id as string,
  };
};

const mapDispatchToProps = {
  push,
  getApexDocuments,
  getDocumentsForBucket,
  getAllAccounts,
  getUserAgreements,
  flushErrors,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  tab: string;
  fetching: boolean;
  showCRBAgreement: boolean;
  showVirtualAccountNumberDisclosure: boolean;
}

class IndividualStatements extends React.PureComponent<Props, State> {
  _promises: unknown[] = [];
  // Tracks which underlying sources we've already fetched, so revisiting a tab
  // (or an unrelated re-render) never refetches its data. Keyed by source
  // rather than tab so the shared APEX fetch is issued only once for both the
  // IRA statements and confirmations tabs.
  _loadedSources = new Set<TabSource>();

  constructor(props: Props) {
    super(props);

    this.state = {
      tab: tabs[0].id,
      fetching: false,
      showCRBAgreement: false,
      showVirtualAccountNumberDisclosure: false,
    };
  }

  componentDidMount() {
    window.analytics.page("Individual Statements");
    // Accounts resolve iraAccountId, which the document tabs need. The current
    // tab's own data is loaded lazily — here if it can be, otherwise from
    // componentDidUpdate once iraAccountId lands in props.
    this._promises.push(
      this.props.getAllAccounts(this.props.client as ApolloClient<object>)
    );
    this._loadTab(this.state.tab);
  }

  componentDidUpdate() {
    // getAllAccounts populates iraAccountId asynchronously; retry the current
    // tab now that it may be available (a no-op once the tab has loaded).
    this._loadTab(this.state.tab);
  }

  // Fetch a tab's data the first time it's shown. Account-scoped sources need
  // iraAccountId; the agreements source pulls user agreements. Guarded by
  // _loadedSources so nothing is fetched twice (and so the APEX fetch shared by
  // the IRA statements and confirmations tabs runs only once).
  _loadTab = (tabId: string) => {
    const source = TAB_SOURCE[tabId];
    if (!source || this._loadedSources.has(source)) {
      return;
    }

    const client = this.props.client as ApolloClient<object>;

    if (source === "agreements") {
      this._loadedSources.add(source);
      this._promises.push(this.props.getUserAgreements(client));
      return;
    }

    // The document sources are account-scoped; wait for iraAccountId to land.
    if (!this.props.iraAccountId) {
      return;
    }
    this._loadedSources.add(source);

    if (source === "apex") {
      // One APEX fetch feeds both the IRA statements and confirmations tabs.
      this._promises.push(
        this.props.getApexDocuments(client, this.props.iraAccountId)
      );
    } else {
      this._promises.push(
        this.props.getDocumentsForBucket(
          client,
          this.props.iraAccountId,
          "prpStatements",
          "CRB"
        )
      );
    }
  };

  componentWillUnmount() {
    this.props.flushErrors();
    each(
      this._promises as Array<{ cancel?: () => void } | null | undefined>,
      (promise) => {
        if (promise && typeof promise.cancel === "function") {
          promise.cancel();
        }
      }
    );
  }

  _tabUpdate(value: { k: string | null }) {
    // The fetch for the newly-selected tab is triggered by componentDidUpdate.
    this.setState({ tab: value.k as string });
  }

  _downloadDoc = (docId: string) => {
    const docUrlPromise = getDownloadUrl(
      this.props.client as ApolloClient<object>,
      this.props.userId,
      docId
    ).then((result) => {
      simulateLinkClickNewTab((result as { url: string }).url, false);
    });

    this._promises.push(docUrlPromise);
  };

  _getIndividualStatementData = (): StatementRow[] => {
    let statementsToUse: StatementLike[] | undefined,
      statementType: "dynamic" | "static" | undefined;
    switch (this.state.tab) {
      case "prpStatements":
        statementsToUse = this.props.prpStatements;
        statementType = "dynamic";
        break;
      case "iraStatements":
        statementsToUse = this.props.iraStatements;
        statementType = "dynamic";
        break;
      case "confirmations":
        statementsToUse = this.props.confirmations;
        statementType = "dynamic";
        break;
      case "agreements":
        statementsToUse = this.getStaticStatements();
        statementType = "static";
        break;
    }

    return map(statementsToUse, (statement) => {
      const createdAt = moment(statement.createdAt).format("MMM Do YYYY");

      return {
        statement,
        id: statement.id,
        createdAt,
        name: statement.name,
        statementsToUse,
        statementType,
      };
    });
  };

  getStaticStatements = (): StaticStatement[] => {
    const staticStatements: StaticStatement[] = [
      {
        createdAt: "2024-11-01",
        name: "Icon ADV Part 2A",
        id: "icon-adv-part-2a",
        url: `https://iconsavingsplan-public.s3-us-west-2.amazonaws.com/${
          isProduction() ? "production" : "staging"
        }/documents/IFS_ADV_Part_2A.pdf`,
      },
      {
        createdAt: "2020-12-18",
        name: "Icon ADV Part 2B",
        id: "icon-adv-part-2b",
        url: `https://iconsavingsplan-public.s3-us-west-2.amazonaws.com/${
          isProduction() ? "production" : "staging"
        }/documents/IFS_ADV_Part_2B.pdf`,
      },
      {
        createdAt: "2024-11-01",
        name: "Icon Form CRS",
        id: "icon-crs",
        url: `https://iconsavingsplan-public.s3-us-west-2.amazonaws.com/${
          isProduction() ? "production" : "staging"
        }/documents/IFS_Form_CRS.pdf`,
      },
      {
        createdAt: "2020-08-25",
        name: "Icon Advisory Agreement",
        id: "icon-adv-agreement",
        url: `https://iconsavingsplan-public.s3-us-west-2.amazonaws.com/${
          isProduction() ? "production" : "staging"
        }/documents/IFS_Advisory_Agreement.pdf`,
      },
      {
        createdAt: "2021-01-01",
        name: "Icon IRA Disclosure Statement",
        id: "icon-disclosure-statement",
        url: `https://iconsavingsplan-public.s3-us-west-2.amazonaws.com/${
          isProduction() ? "production" : "staging"
        }/documents/ICON_IRA_DISCLOSURE_STATEMENT.pdf`,
      },
    ];

    if (!this.props.is401kRollin) {
      staticStatements.push({
        createdAt: this.props.crbAgreementDate,
        name: "Cross River Bank Deposit Account Agreement",
        id: "crb-agreement",
        onClick: () => {
          this.setState({ showCRBAgreement: true });
        },
      });
    } else {
      staticStatements.push({
        createdAt: this.props.virtualAccountNumberDisclosureDate,
        name: "Virtual Account Number Disclosure",
        id: "virtual-account-number-disclosure",
        onClick: () => {
          this.setState({ showVirtualAccountNumberDisclosure: true });
        },
      });
    }

    return staticStatements;
  };

  _buildIndividualStatementColumns = (): Array<
    IconTableColumn<StatementRow>
  > => {
    const columns: Array<IconTableColumn<StatementRow>> = [
      {
        label: "Statement Date",
        key: "createdAt",
      },

      {
        label: "Name",
        key: "name",
      },

      {
        customComponent: (props: StatementRow) => {
          let action;

          if (props.statementType === "dynamic") {
            action = (
              <a
                className="link"
                onClick={() => {
                  this._downloadDoc(props.id);
                }}
              >
                <FiDownload
                  size={"20px"}
                  color={"#FFFFFF"}
                  fill={"#FFFFFF"}
                  stroke={"#635bff"}
                  strokeWidth={"1.5"}
                  className="icon"
                />
              </a>
            );
          } else if (props.statementType === "static") {
            const staticStatement = props.statement as StaticStatement;
            if (staticStatement.url) {
              action = (
                <a
                  className="link"
                  href={staticStatement.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiDownload
                    size={"20px"}
                    color={"#FFFFFF"}
                    fill={"#FFFFFF"}
                    stroke={"#635bff"}
                    strokeWidth={"1.5"}
                    className="icon"
                  />
                </a>
              );
            }

            if (staticStatement.onClick) {
              action = (
                <a className="link" onClick={staticStatement.onClick}>
                  <FiEye
                    size={"20px"}
                    color={"#FFFFFF"}
                    fill={"#FFFFFF"}
                    stroke={"#635bff"}
                    strokeWidth={"1.5"}
                    className="icon"
                  />
                </a>
              );
            }
          }

          return <div className="undo-button">{action}</div>;
        },
      },
    ];
    return columns;
  };

  _controlledTabs() {
    return (
      <Tabs
        activeKey={this.state.tab}
        id="tab-company"
        onSelect={(k) => this._tabUpdate({ k })}
      >
        {map(tabs, (tab) => {
          return <Tab key={tab.id} eventKey={tab.id} title={tab.label} />;
        })}
      </Tabs>
    );
  }

  render() {
    const columns = this._buildIndividualStatementColumns();
    const data = this._getIndividualStatementData();

    const showBlankState = isEmpty(data);

    return (
      <div className="mega-container dashboard-content" id="statements">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Statements and Account Documents</h1>
            <p className="page-subtext">
              See and download all your statements and documents
            </p>
            <div className="main-content">
              {this.state.showCRBAgreement && (
                <TermsModal
                  termsInfo={{}}
                  type="crbAgreement"
                  show={true}
                  onClose={() => this.setState({ showCRBAgreement: false })}
                />
              )}
              {this.state.showVirtualAccountNumberDisclosure && (
                <TermsModal
                  termsInfo={{}}
                  type="virtualAccountNumberDisclosure"
                  show={true}
                  onClose={() =>
                    this.setState({ showVirtualAccountNumberDisclosure: false })
                  }
                />
              )}
              <Row>
                <Col>
                  <div className="card-tab">{this._controlledTabs()}</div>
                  {TAB_BANNERS[this.state.tab] && (
                    <IconBanner
                      subheader={TAB_BANNERS[this.state.tab]}
                      circle={false}
                    />
                  )}
                  <Card className="dashboard-widget statements-table">
                    <Card.Body>
                      {this.props.isFetching && <IconSpinner centered />}
                      {!this.props.isFetching &&
                        showBlankState &&
                        !this.props.error && (
                          <IconMessageCard
                            header={`No ${
                              tabs.find((t) => t.id === this.state.tab)
                                ?.label ?? "documents"
                            } to show at this time`}
                            icon={
                              <FiList
                                color="white"
                                stroke="#60A4BF"
                                size={16}
                              />
                            }
                          />
                        )}
                      {!this.props.isFetching && this.props.error && (
                        <div className="alert-container">
                          <Alert type="error" msg={this.props.error} />
                        </div>
                      )}
                      {!this.props.isFetching &&
                        !showBlankState &&
                        !this.props.error && (
                          <IconTable columns={columns} data={data} />
                        )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualStatements));
